import {
  Button,
  Input,
  Modal,
  Radio,
  ReactSelect,
  TextArea,
  Typography,
} from "@anchor/react-components/dist/lib/components";
import React from "react";
import {
  rootCauseCodes,
  rootCauseSubCodes,
  rootCauseType2Config,
} from "../../constants/rootCauseCodesAndSubCodes";
import { vendors } from "../../constants/vendors";
import "./rcstyles.css";

import UserInfo from "../../context/UserAccessContext";
import { IsLocked } from "../IsLocked/IsLocked";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core";
import { getConfidenceLevel } from "../Report/RecurrenceAnalysisPanel";
import RecurrenceMatchFeedback, { MatchTitle, getLatestVerdict } from "../Report/RecurrenceMatchFeedback";
// import { useWebSocket } from "../../context/WebSocketContext/WebSocketContext";

const MVC_VULNERABILITY_OPTIONS = [
  { id: "no_trp", label: "No TRP", value: "No TRP" },
  { id: "no_trp", label: "NA", value: "NA" },
  { id: "ot_eol", label: "OT - EOL", value: "OT - EOL" },
  { id: "tech_eol", label: "Tech - EOL", value: "Tech - EOL" },
  { id: "ot_spof", label: "OT - SPOF", value: "OT - SPOF" },
  { id: "tech_spof", label: "Tech - SPOF", value: "Tech - SPOF" },
  { id: "no_mon_vm", label: "No Monitoring and Alerting on VM/Server level", value: "No Monitoring and Alerting on VM/Server level" },
  { id: "no_mon_app", label: "No Monitoring and Alerting on Application level", value: "No Monitoring and Alerting on Application level" },
  { id: "support_inadequate", label: "Support not adequate for business requirements", value: "Support not adequate for business requirements" },
];


function RecurrenceSuggestions({ suggestions, onFindSimilarIncidents, onAcceptSuggestion, onRejectSuggestion, selectedIncidentIds = [] }) {
  const status = suggestions?.status || "NOT_STARTED";
  const matches = suggestions?.result?.matches || [];

  if (status === "PENDING" || status === "QUEUED") {
    return (
      <div style={{ marginTop: "16px", fontSize: "13px", color: "#545454" }}>
        Searching for similar incidents…
      </div>
    );
  }

  if (status === "FAILED") {
    return (
      <div style={{ marginTop: "16px", fontSize: "13px", color: "#b80012" }}>
        {suggestions?.error || "Unable to load suggestions."}
      </div>
    );
  }

  if (status === "NOT_STARTED") {
    return (
      <div style={{ marginTop: "16px" }}>
        <McButton
          appearance="neutral"
          variant="outlined"
          fit="small"
          icon="sparkle"
          label="Find similar incidents"
          onClick={onFindSimilarIncidents}
        />
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div style={{ marginTop: "16px", fontSize: "13px", color: "#545454" }}>
        AI search found no similar past incidents.
      </div>
    );
  }

  return (
    <div style={{ marginTop: "16px" }}>
      <Typography variant="h6" fontStyle="bold">AI-suggested recurring incidents</Typography>
      {matches.map((match) => {
        const level = getConfidenceLevel(match.confidence);
        const isSelected = selectedIncidentIds.includes(match.incidentId);
        const latest = getLatestVerdict(suggestions?.feedback, match.incidentId);
        // The form's list is the source of truth for accepted; only rejections come from saved feedback.
        const verdict = isSelected ? { verdict: "ACCEPTED" } : latest?.verdict === "REJECTED" ? latest : null;
        return (
          <div
            key={`${match.incidentId}-${match.postMortemId || ""}`}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              padding: "8px 10px",
              marginTop: "6px",
              border: isSelected ? "1px solid #0073ab" : "1px solid #e2e2e2",
              borderRadius: "4px",
            }}
          >
            <div>
              <div style={{ fontWeight: 600 }}>
                <MatchTitle match={match} />
                {level && <span style={{ marginLeft: "8px", fontWeight: 600, color: level.color }}>{level.label}</span>}
              </div>
              {Array.isArray(match.matchingEvidence) && match.matchingEvidence.length > 0 && (
                <div style={{ fontSize: "12px", color: "#545454", marginTop: "2px" }}>
                  {match.matchingEvidence.join("; ")}
                </div>
              )}
            </div>
            <RecurrenceMatchFeedback
              match={match}
              verdict={verdict}
              onAccept={onAcceptSuggestion}
              onReject={onRejectSuggestion}
            />
          </div>
        );
      })}
    </div>
  );
}

function RcModal({
  handleModal,
  rcModal,
  handleRcData,
  rcData,
  impactedByChange,
  handleChangeIsRequired,
  onSave,
  recurrenceIsRequired,
  handleRecurrenceChange,
  handleRecurreceData,
  recurrenceData,
  recurrenceSuggestions,
  onFindSimilarIncidents,
  onAcceptSuggestion,
  onRejectSuggestion,
  referencesList,
  referenceErrors,
  onAddReference,
  onReferenceChange,
  onRemoveReference,
  onValidateReference,
  showRecurrenceSuggestions,
  invalidTicket,
  validating,
  lockedFields,
  postmortemID,
  incidentID,
  savingRef,
  portfolio,
  mvcVulnerabilityRelated,
  dispatch,
}) {
  // const { lockField, unlockField } = useWebSocket();
  const lockField = null; // WebSocket disabled
  const unlockField = null; // WebSocket disabled

  const { graphData } = UserInfo();
  const recurrenceLockedByOther = !!(lockedFields || []).find(
    (field) =>
      (field.fieldName === "incident_id" || field.fieldName === "problem_id") &&
      field.lockedBy !== graphData.username
  );

  const isType2 = rcData && rcData.rootCauseCode && rootCauseType2Config[rcData.rootCauseCode.value];
  const subCategoryOptions = isType2 ? rootCauseType2Config[rcData.rootCauseCode.value].subCategories : [];
  const subCodeOptions = isType2
    ? rcData.subCategory
      ? rootCauseType2Config[rcData.rootCauseCode.value].subCodes[rcData.subCategory.value] || []
      : []
    : rootCauseSubCodes[rcData.rootCauseCode.value];

  // Derive vendor yes/no state from rcData
  const vendorRelated = rcData.causedByVendor && (rcData.causedByVendor.value === "Yes" || rcData.causedByVendor.value === "YES");

  const isAPMTPortfolio = portfolio?.value === "APMT, IOT and Automation";
  const vendorSelection = rcData.causedByVendor?.value || "";
  const hasReferenceErrors = Object.values(referenceErrors || {}).some(Boolean);
  const handleMvcVulnerabilityChange = (selectedValues) => {
    if (typeof dispatch === "function") {
      dispatch({
        type: "HANDLE EDITOR CHANGE",
        field: "mvcVulnerabilityRelated",
        payload: selectedValues || [],
      });
    }
  };

  const handleVendorChange = (value) => {
    const newVendorValue = value === "Yes";
    const newVendorObject = {
      id: value,
      label: value,
      value,
    };

    // If changing to "No", clear vendor details
    if (!newVendorValue) {
      handleRcData({ target: { name: "vendorDetails", value: "" } });
      handleRcData({ target: { name: "customVendorName", value: "" } });
    }

    handleRcData(newVendorObject, "causedByVendor");
  };

  const handleFieldLocking = (fieldName) => {
    // if (fieldName !== "rc_cause") {
    //   return;
    // }
    // lockField(fieldName, graphData.username);
    // WebSocket disabled
  };

  const handleFieldUnlocking = (fieldName) => {
    // WebSocket disabled
    // if(fieldName === "incident_id" || fieldName === "problem_id") {
    //   unlockField(fieldName, recurrenceData)
    //   return
    // }
    // unlockField(fieldName, rcData);


  };



  return (
    <div>
      <Modal
        width="1500"
        height={impactedByChange ? "800" : "600"}
        backdropcloseactiondisabled={true}
        actions={{
          primaryAction: (
            <Button
              label="OK"
              onClick={onSave}
              appearance="alt"
              disabled={
                !vendorSelection ||
                impactedByChange === null ||
                recurrenceIsRequired === null ||
                invalidTicket.changeId.inValid ||
                validating.changeId ||
                hasReferenceErrors
              }
            />
          ),
          // secondaryAction: (
          //   <Button label="Back" onClick={handleModal} variant="outlined" />
          // ),
        }}
        heading={
          <Typography
            variant="h3"
            fontStyle="bold"
            style={{ textAlign: "center", marginTop: "10px" }}
          >
            Root Cause
          </Typography>
        }
        onClose={() => {
          handleModal();
        }}
        showCloseIcon={true}
        open={rcModal}
      >
        <div className="scrollbar">
          <div className="">
            <div className="rc-inputWrapper">
              <div style={{ marginBottom: "0px" }}>
                <ReactSelect
                  className="react-select"
                  closeMenuOnSelect
                  customNoOptionsMessage={<p>Not a valid option</p>}
                  onFocus={() => handleFieldLocking("rc_cause_code")}
                  onBlur={() => handleFieldUnlocking("rc_cause_code")}
                  disabled={lockedFields.find(
                    (field) =>
                      field.fieldName === "rc_cause_code" &&
                      field.lockedBy !== graphData.username
                  )}
                  fit="medium"
                  id="selectAction"
                  isSearchable
                  label={
                    <Typography variant="h6" fontStyle="bold" style={{}}>
                      Root Cause Code
                    </Typography>
                  }
                  name="rootCauseCode"
                  onChange={(e) => handleRcData(e, "rootCauseCode")}
                  onInputChange={function noRefCheck() { }}
                  onMenuClose={function noRefCheck() { }}
                  onMenuOpen={function noRefCheck() { }}
                  onOptionUpdate={function noRefCheck() { }}
                  options={rootCauseCodes}
                  orientation="vertical"
                  required
                  suggestType="static"
                  value={rcData.rootCauseCode}
                  variant="default"
                  width={100}
                />
                <IsLocked
                  field="rc_cause_code"
                  lockedFields={lockedFields}
                  graphData={graphData}
                />
              </div>
              {isType2 && (
                <div style={{ marginBottom: "0px" }}>
                  <ReactSelect
                    className="react-select"
                    closeMenuOnSelect
                    customNoOptionsMessage={<p>Not a valid option</p>}
                    onFocus={() => handleFieldLocking("rc_cause_sub_category")}
                    onBlur={() => handleFieldUnlocking("rc_cause_sub_category")}
                    disabled={lockedFields.find(
                      (field) =>
                        field.fieldName === "rc_cause_sub_category" &&
                        field.lockedBy !== graphData.username
                    )}
                    fit="medium"
                    id="selectSubCategory"
                    isSearchable
                    label={<Typography variant="h6" fontStyle="bold">Sub-Category</Typography>}
                    name="subCategory"
                    onChange={(e) => handleRcData(e, "subCategory")}
                    options={subCategoryOptions}
                    orientation="vertical"
                    required
                    suggestType="static"
                    value={rcData.subCategory}
                    variant="default"
                    width={100}
                  />
                  <IsLocked
                    field="rc_cause_sub_category"
                    lockedFields={lockedFields}
                    graphData={graphData}
                  />
                </div>
              )}
              <div style={{ marginBottom: "0px" }}>
                <ReactSelect
                  className="react-select"
                  closeMenuOnSelect
                  customNoOptionsMessage={<p>Not a valid option</p>}
                  onFocus={() => handleFieldLocking("rc_cause_sub_code")}
                  onBlur={() => handleFieldUnlocking("rc_cause_sub_code")}
                  disabled={lockedFields.find(
                    (field) =>
                      field.fieldName === "rc_cause_sub_code" &&
                      field.lockedBy !== graphData.username
                  )}
                  fit="medium"
                  id="selectAction"
                  isSearchable
                  label={
                    <Typography variant="h6" fontStyle="bold" style={{}}>
                      Root Cause Sub Code
                    </Typography>
                  }
                  name="rootCauseSubCode"
                  onChange={(e) => handleRcData(e, "rootCauseSubCode")}
                  onInputChange={function noRefCheck() { }}
                  onMenuClose={function noRefCheck() { }}
                  onMenuOpen={function noRefCheck() { }}
                  onOptionUpdate={function noRefCheck() { }}
                  options={subCodeOptions}
                  orientation="vertical"
                  required
                  suggestType="static"
                  value={rcData.rootCauseSubCode}
                  variant="default"
                  width={100}
                />
                <IsLocked
                  field="rc_cause_sub_code"
                  lockedFields={lockedFields}
                  graphData={graphData}
                />
              </div>

              {isAPMTPortfolio && (
                <div style={{ marginBottom: "0px" }}>
                  <ReactSelect
                    className="react-select"
                    closeMenuOnSelect={false}
                    customNoOptionsMessage={<p>Not a valid option</p>}
                    fit="medium"
                    id="selectMvcVulnerabilityRelated"
                    isSearchable
                    isMulti
                    label={
                      <Typography variant="h6" fontStyle="bold" style={{}}>
                        MVC Vulnerability Related
                      </Typography>
                    }
                    name="mvcVulnerabilityRelated"
                    onChange={(selectedValues) => handleMvcVulnerabilityChange(selectedValues || [])}
                    onInputChange={function noRefCheck() { }}
                    onMenuClose={function noRefCheck() { }}
                    onMenuOpen={function noRefCheck() { }}
                    onOptionUpdate={function noRefCheck() { }}
                    options={MVC_VULNERABILITY_OPTIONS}
                    orientation="vertical"
                    suggestType="static"
                    value={mvcVulnerabilityRelated || []}
                    variant="default"
                    width={100}
                  />
                </div>
              )}
            </div>

            {/* Vendor Details Section - Always Visible */}
            <div style={{ marginTop: "20px" }}>
              <Typography variant="h6" fontStyle="bold" style={{}}>
              </Typography>
              <Typography variant="h6" fontStyle="bold" style={{ marginTop: "10px" }}>
                Caused by Vendor <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <Radio
                checked={vendorSelection === "Yes"}
                label="Yes"
                onClick={() => handleVendorChange("Yes")}
                onChange={() => handleVendorChange("Yes")}
                type="standard"
              />
              <Radio
                checked={vendorSelection === "No"}
                label="No"
                onClick={() => handleVendorChange("No")}
                onChange={() => handleVendorChange("No")}
                type="standard"
              />

              {vendorRelated && (
                <div style={{ marginTop: "10px" }}>
                  <ReactSelect
                    className="react-select"
                    closeMenuOnSelect
                    customNoOptionsMessage={<p>Not a valid option</p>}
                    fit="medium"
                    id="selectAction"
                    isSearchable
                    label="Vendor Name"
                    name="tags"
                    onChange={(e) => {
                      handleRcData(e.value, "vendorDetails");
                      // Clear custom vendor input when switching from "Other" to predefined vendor
                      if (e.value !== "Other" && rcData.vendorDetails === "Other") {
                        handleRcData("", "customVendorName");
                      }
                    }}
                    onInputChange={function noRefCheck() { }}
                    onMenuClose={function noRefCheck() { }}
                    onMenuOpen={function noRefCheck() { }}
                    onOptionUpdate={function noRefCheck() { }}
                    options={vendors}
                    orientation="vertical"
                    required
                    suggestType="static"
                    value={{
                      id: rcData.vendorDetails === "Other" || vendors.some(v => v.value === rcData.vendorDetails) ? rcData.vendorDetails : "Other",
                      label: rcData.vendorDetails === "Other" || vendors.some(v => v.value === rcData.vendorDetails) ? rcData.vendorDetails : "Other",
                      value: rcData.vendorDetails === "Other" || vendors.some(v => v.value === rcData.vendorDetails) ? rcData.vendorDetails : "Other",
                    }}
                    variant="default"
                    width={100}
                    onFocus={() => handleFieldLocking("rc_vendor_details")}
                    onBlur={() => handleFieldUnlocking("rc_vendor_details")}
                    disabled={lockedFields.find(
                      (field) =>
                        field.fieldName === "rc_vendor_details" &&
                        field.lockedBy !== graphData.username
                    )}
                  />
                  {rcData.vendorDetails === "Other" || rcData.customVendorName ? (
                    <div style={{ marginTop: "10px" }}>
                      <Input
                        onChange={(e) => {
                          handleRcData(e, null);
                        }}
                        value={rcData.customVendorName || ""}
                        placeholder="Enter vendor name"
                        name="customVendorName"
                        label="Custom Vendor Name"
                        required
                        onFocus={() => handleFieldLocking("rc_vendor_details")}
                        onBlur={() => handleFieldUnlocking("rc_vendor_details")}
                        disabled={lockedFields.find(
                          (field) =>
                            field.fieldName === "rc_vendor_details" &&
                            field.lockedBy !== graphData.username
                        )}
                      />
                    </div>
                  ) : null}
                </div>
              )}
              <IsLocked
                field="rc_vendor_details"
                lockedFields={lockedFields}
                graphData={graphData}
              />
            </div>

            {/* Caused by Change Section */}
            <div style={{ marginTop: "20px" }}>
              <Typography variant="h6" fontStyle="bold" style={{}}>
                Caused by Change <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              {/* <label></label> */}
              <Radio
                checked={impactedByChange === true}
                label="Yes"
                onClick={() => handleChangeIsRequired("Yes")}
                onChange={() => handleChangeIsRequired("Yes")}
                type="standard"
              />
              <Radio
                checked={impactedByChange === false}
                label="No"
                onClick={() => handleChangeIsRequired("No")}
                onChange={() => handleChangeIsRequired("No")}
                type="standard"
              />
            </div>
          </div>
          {impactedByChange && (
            <div
              style={{ marginTop: "10px" }}
              className="rc-inputWrapper-changeRelated"
            >
              <div style={{ marginBottom: "10px" }}>
                <Input
                  onChange={handleRcData}
                  name="causedByChangeNumber"
                  value={rcData.causedByChangeNumber}
                  label="Caused By Change Number"
                  hint={
                    validating.changeId
                      ? "Validating Change Id..."
                      : invalidTicket.changeId.invalidMessage.length === 0
                        ? "Valid ticket Id"
                        : ""
                  }
                  error={invalidTicket.changeId.inValid}
                  errorMessage={invalidTicket.changeId.invalidMessage}
                  onFocus={() => handleFieldLocking("rc_change_number")}
                  onBlur={() => handleFieldUnlocking("rc_change_number")}
                  disabled={lockedFields.find(
                    (field) =>
                      field.fieldName === "rc_change_number" &&
                      field.lockedBy !== graphData.username
                  )}
                />
                <IsLocked
                  field="rc_change_number"
                  lockedFields={lockedFields}
                  graphData={graphData}
                />
              </div>
            </div>
          )}
          <div style={{ marginTop: "20px" }}>
            <Typography variant="h6" fontStyle="bold" style={{}}>
              Recurrence <span style={{ color: "#d32f2f" }}>*</span>
            </Typography>

            <Radio
              checked={recurrenceIsRequired === true}
              label="Yes"
              onClick={() => handleRecurrenceChange("Yes")}
              onChange={() => handleRecurrenceChange("Yes")}
              type="standard"
            />
            <Radio
              checked={recurrenceIsRequired === false}
              label="No"
              onClick={() => handleRecurrenceChange("No")}
              onChange={() => handleRecurrenceChange("No")}
              type="standard"
            />
          </div>
          {recurrenceIsRequired && (
            <>
              {showRecurrenceSuggestions && (
                <RecurrenceSuggestions
                  suggestions={recurrenceSuggestions}
                  onFindSimilarIncidents={onFindSimilarIncidents}
                  onAcceptSuggestion={onAcceptSuggestion}
                  onRejectSuggestion={onRejectSuggestion}
                  selectedIncidentIds={(referencesList || []).map((reference) => reference.incidentId)}
                />
              )}
              <div style={{ marginTop: "20px" }}>
                <Typography variant="h6" fontStyle="bold">Earlier incidents this repeats</Typography>
                {(referencesList || []).length === 0 && (
                  <div style={{ fontSize: "13px", color: "#545454", margin: "6px 0" }}>
                    None added yet. Accept a suggestion above or add one manually.
                  </div>
                )}
                {(referencesList || []).map((reference, index) => (
                  <div
                    key={index}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr auto",
                      gap: "12px",
                      alignItems: "start",
                      marginTop: "10px",
                    }}
                  >
                    <Input
                      onChange={(e) => onReferenceChange(index, "incidentId", e.target.value)}
                      onBlur={() => onValidateReference(index)}
                      disabled={recurrenceLockedByOther}
                      name={`incidentId-${index}`}
                      value={reference.incidentId}
                      label="Earlier incident this repeats (Incident ID)"
                      error={!!referenceErrors?.[index]}
                      errorMessage={referenceErrors?.[index] || ""}
                    />
                    <Input
                      onChange={(e) => onReferenceChange(index, "problemId", e.target.value)}
                      disabled={recurrenceLockedByOther}
                      name={`problemId-${index}`}
                      value={reference.problemId}
                      label="Linked problem record (Problem ID)"
                    />
                    <div style={{ paddingTop: "22px" }}>
                      <McButton
                        appearance="neutral"
                        variant="plain"
                        fit="small"
                        label="Remove"
                        disabled={recurrenceLockedByOther}
                        onClick={() => onRemoveReference(index)}
                      />
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: "10px" }}>
                  <McButton
                    appearance="neutral"
                    variant="outlined"
                    fit="small"
                    label="Add another incident"
                    disabled={recurrenceLockedByOther}
                    onClick={onAddReference}
                  />
                </div>
              </div>
              <IsLocked
                field="recurrence"
                lockedFields={lockedFields}
                graphData={graphData}
              />
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default RcModal;
